import React from 'react';
import { useParams } from 'react-router';
import Button from '@mui/material/Button';
import DataList from 'src/modules/DataList';
import Loader from '@/components/common/Loader';
import OpportunityStatus from '@/constants/OpportunityStatus';
import CreateEditDialogType from '@/constants/ui/CreateEditDialogType';
import translate from '@/lib/intl/translate';
import { DataListTableHeader } from '@/modules/DataList';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { useEntityDetails } from '@/modules/EntityDetails';
import {
  OpportunitiesSortableFields,
  useCreateEditOpportunityDialog,
  useOpportunitiesDataListColumns,
  useOpportunitiesDataListData,
  type OpportunitiesDataListRow,
} from '@/modules/Opportunities';
import useConstituentFilteredOpportunities from '../hooks/useConstituentFilteredOpportunities';
import ConstituentOpportunitiesStatusSelect from './ConstituentOpportunitiesStatusSelect';

function ConstituentOpportunitiesDataList() {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const closeCreateOpportunityDialog = React.useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const openCreateOpportunityDialog = React.useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  useCreateEditOpportunityDialog(isDialogOpen, {
    onClose: closeCreateOpportunityDialog,
    type: CreateEditDialogType.CREATE,
  });

  const { constituentId } = useParams<{ constituentId: string }>();
  const { openOpportunityEntity } = useEntityDetails();
  const { isLagging, isLoading, opportunities, totalHits } =
    useOpportunitiesDataListData({ constituentId });

  const [statusFilters, setStatusFilters] = React.useState({
    [OpportunityStatus.ACTIVE]: true,
    [OpportunityStatus.ARCHIVED]: false,
  });

  const filteredOpportunities = useConstituentFilteredOpportunities(
    opportunities,
    statusFilters,
  );

  const handleRowClick = React.useCallback((row: OpportunitiesDataListRow) => {
    openOpportunityEntity(row.opportunityId);
  }, []);

  const columns = useOpportunitiesDataListColumns({
    showConstituent: false,
    shouldRemoveOnArchive: false,
    shouldRemoveOnUnarchive: false,
  });

  if (isLoading) {
    return <Loader isCentered />;
  }

  if (!totalHits) {
    return null;
  }

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between">
        <DataListTableHeader
          text="OPPORTUNITIES_TABLE_HEADER"
          count={filteredOpportunities.length}
        />
        <ConstituentOpportunitiesStatusSelect
          statusFilters={statusFilters}
          onSubmit={setStatusFilters}
        />
      </div>
      {filteredOpportunities.length ? (
        <DataList<OpportunitiesDataListRow>
          TableRowProps={({ archivedAt }) => ({
            onClick: handleRowClick,
            className: `${
              archivedAt &&
              'italic [&_td]:text-text-disabled [&_td]:hover:text-text-secondary'
            }`,
            hover: true,
          })}
          columns={columns}
          data={filteredOpportunities}
          isLagging={isLagging}
          rowKey="opportunityId"
          sortableFields={OpportunitiesSortableFields}
          totalRows={filteredOpportunities.length}
          hidePageSize
        />
      ) : (
        <div>
          {statusFilters[OpportunityStatus.ARCHIVED] ? (
            <p>{translate('OPPORTUNITIES_NO_ARCHIVED')}</p>
          ) : (
            <Button onClick={openCreateOpportunityDialog} className="p-0">
              {translate('OPPORTUNITIES_ADD_AN_OPPORTUNITY')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default ConstituentOpportunitiesDataList;
